import * as React from 'react';
import ProductInfoTooltip from 'lib/components/ProductInfoTooltip/ProductInfoTooltip';
import { HoverPopup } from 'lib/components/HoverPopup';
import { MdLock } from 'react-icons/md';
import { PackageNameLabel } from 'lib/utils/productFeature';
import { Typography } from 'react-covideo-common';
import {
  IconWrap,
  LabelWrap,
  LockRow,
  SegementedWrapper,
  SegmentedControlItem,
  TextWrap,
} from './styles';
import { SegementedControlProps } from './types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LiveItem = ({ label }: { label: React.ReactNode }) => {
  const { t } = useTranslation();
  return (
    <SegmentedControlItem isLive>
      <HoverPopup
        width='120px'
        padding='8px 12px'
        popup={<>{t('Available Soon')}</>}
        hoverable={
          <LabelWrap>
            <Typography
              ellipsis
              fontWeight={600}
              variant='small'
              align='center'
            >
              {label}
            </Typography>
          </LabelWrap>
        }
      />
    </SegmentedControlItem>
  );
};

const LockedItem = ({
  label,
  nextPlan,
}: {
  label: React.ReactNode;
  nextPlan: string;
}) => (
  <SegmentedControlItem isLocked>
    <ProductInfoTooltip nextPlan={nextPlan} direction='bottom'>
      <LockRow>
        <TextWrap>
          <Typography ellipsis fontWeight={600} variant='small' align='center'>
            {label}
          </Typography>
        </TextWrap>
        <IconWrap>
          <MdLock size={16} color='inherit' />
        </IconWrap>
      </LockRow>
    </ProductInfoTooltip>
  </SegmentedControlItem>
);

const DisabledFragment = ({ label }: { label: React.ReactNode }) => (
  <SegmentedControlItem disabled>
    <LabelWrap>
      <Typography ellipsis fontWeight={600} variant='small' align='center'>
        {label}
      </Typography>
    </LabelWrap>
  </SegmentedControlItem>
);

const SegmentedItem = ({
  isActive,
  onClick,
  label,
}: {
  isActive: boolean;
  onClick: () => void | null;
  label: React.ReactNode;
}) => (
  <SegmentedControlItem active={isActive} onClick={onClick}>
    <LabelWrap>
      <Typography ellipsis fontWeight={600} variant='small' align='center'>
        {label}
      </Typography>
    </LabelWrap>
  </SegmentedControlItem>
);

const PUBLISH = 'Publish';

export const SegmentedControl = ({
  initialValue,
  values,
  onChange,
  isLoading = false,
  extendStyles,
}: SegementedControlProps) => {
  const [selectedValue, setSelectedValue] = useState(() => initialValue);

  const handleSelect = (value: string) => {
    // optional safety, prevent duplicate tracking
    if (isLoading || value === selectedValue) {
      return;
    }
    onChange?.(value);
    setSelectedValue(value);
  };

  // Filter hidden upfront; index is now reliable for the pill position
  const visibleItems = useMemo(() => values.filter(v => !v.isHidden), [values]);

  return (
    <SegementedWrapper isLoading={isLoading} style={extendStyles?.wrapper}>
      {visibleItems.map((item, index) => {
        const { value, label, isLocked, isLive = true, isDisabled } = item;

        const isActive = value === selectedValue;
        const nextPlan =
          value === PUBLISH ? PackageNameLabel.TEAMS : PackageNameLabel.PRO;

        if (!isLive) {
          return <LiveItem key={`pill-${index}`} label={label} />;
        }

        if (isLocked) {
          return (
            <LockedItem
              key={`pill-${index}`}
              label={label}
              nextPlan={nextPlan}
            />
          );
        }

        if (isDisabled) {
          return <DisabledFragment key={`pill-${index}`} label={label} />;
        }

        return (
          <SegmentedItem
            isActive={isActive}
            onClick={() => handleSelect(value)}
            label={label}
          />
        );
      })}
    </SegementedWrapper>
  );
};
